import { absoluteAsset, absoluteUrl, SITE_CONFIG } from './site-config';

export type PageMeta = {
  path: string;
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogType?: 'website' | 'article';
  robots?: string;
  breadcrumb?: { name: string; path: string }[];
  product?: {
    name: string;
    applicationCategory: string;
    operatingSystem: string;
    url: string;
  };
};

const TITLE_SUFFIX = ` | ${SITE_CONFIG.name}`;

export const PAGE_META: Record<string, PageMeta> = {
  '/': {
    path: '/',
    title: `${SITE_CONFIG.name} - ${SITE_CONFIG.tagline}`,
    description:
      'Software Lab delivers custom software, AI architecture, and cloud solutions across Europe. We build client platforms and operate B2B SaaS products (Nisium, SiloPass, XnGo).',
    keywords:
      'software development, AI architecture, LLM integration, RAG, cloud services, IT consulting, B2B SaaS, custom software, finance software, healthcare software, hospitality technology, security software, Europe',
    ogImageAlt: 'Software Lab - custom software development and B2B SaaS products',
  },
  '/privacy-policy': {
    path: '/privacy-policy',
    title: `Privacy Policy${TITLE_SUFFIX}`,
    description:
      'Software Lab Privacy Policy - learn how we collect, use, and protect your personal information when you visit softwarelabbg.com or contact us.',
    robots: 'index, follow',
    breadcrumb: [
      { name: 'Home', path: '/' },
      { name: 'Privacy Policy', path: '/privacy-policy' },
    ],
  },
  '/cookie-policy': {
    path: '/cookie-policy',
    title: `Cookie Policy${TITLE_SUFFIX}`,
    description:
      'Software Lab Cookie Policy - learn how we use essential and analytics cookies on our website and how you can manage your preferences.',
    robots: 'index, follow',
    breadcrumb: [
      { name: 'Home', path: '/' },
      { name: 'Cookie Policy', path: '/cookie-policy' },
    ],
  },
  '/products/nisium': {
    path: '/products/nisium',
    title: `Nisium - NIS2 Compliance Platform${TITLE_SUFFIX}`,
    description:
      'Nisium is a B2B GRC SaaS for NIS2 compliance: incident reporting timelines, gap analysis, evidence locker, and vendor risk management. Built by Software Lab.',
    keywords: 'NIS2, GRC, compliance platform, Bulgaria, incident reporting, vendor risk, CERT-BG',
    ogImage: '/assets/products/nisium.png',
    ogImageAlt: 'Nisium NIS2 gap analysis dashboard',
    breadcrumb: [
      { name: 'Home', path: '/' },
      { name: 'Products', path: '/#products' },
      { name: 'Nisium', path: '/products/nisium' },
    ],
    product: {
      name: 'Nisium',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://nisium.com',
    },
  },
  '/products/silopass': {
    path: '/products/silopass',
    title: `SiloPass - Project-Centric Password Manager${TITLE_SUFFIX}`,
    description:
      'SiloPass isolates credentials by project with zero-knowledge encryption, team roles, and EU hosting. Free plan available. Built by Software Lab.',
    keywords: 'password manager, zero-knowledge, project silos, team credentials, EU hosting, B2B SaaS',
    ogImage: '/assets/products/silopass.png',
    ogImageAlt: 'SiloPass project silos dashboard',
    breadcrumb: [
      { name: 'Home', path: '/' },
      { name: 'Products', path: '/#products' },
      { name: 'SiloPass', path: '/products/silopass' },
    ],
    product: {
      name: 'SiloPass',
      applicationCategory: 'SecurityApplication',
      operatingSystem: 'Web',
      url: 'https://silopass.app',
    },
  },
  '/products/xngo': {
    path: '/products/xngo',
    title: `XnGo - Gym & Studio Management Platform${TITLE_SUFFIX}`,
    description:
      'XnGo helps fitness and wellness businesses manage schedules, memberships, and client bookings with admin web, client app, and barcode check-in. Built by Software Lab, Sofia.',
    keywords: 'gym software, studio management, fitness booking, Bulgaria, membership, schedule',
    ogImage: '/assets/products/xngo.png',
    ogImageAlt: 'XnGo admin calendar and studio management',
    breadcrumb: [
      { name: 'Home', path: '/' },
      { name: 'Products', path: '/#products' },
      { name: 'XnGo', path: '/products/xngo' },
    ],
    product: {
      name: 'XnGo',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, iOS, Android',
      url: 'https://xngo.app',
    },
  },
  '/brand': {
    path: '/brand',
    title: `Brand Assets & Logo Kit${TITLE_SUFFIX}`,
    description:
      'Download official Software Lab brand assets: logos, color palette, favicon, and social media templates for press, partners, and marketing.',
    robots: 'noindex, follow',
  },
  '/services/ai-architecture': {
    path: '/services/ai-architecture',
    title: `AI Architecture${TITLE_SUFFIX}`,
    description:
      'Software Lab designs LLM workflows, RAG pipelines, and agent systems with data boundaries, evaluation, and EU-friendly deployment. Practical AI architecture for B2B products.',
    keywords:
      'AI architecture, LLM integration, RAG, AI agents, vector database, Azure OpenAI, OpenAI, AI consulting, enterprise AI',
    breadcrumb: [
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/#Services' },
      { name: 'AI Architecture', path: '/services/ai-architecture' },
    ],
  },
};

const NOT_FOUND_META: PageMeta = {
  path: '/404',
  title: `Page Not Found${TITLE_SUFFIX}`,
  description: 'The page you requested could not be found on softwarelabbg.com.',
  robots: 'noindex, nofollow',
};

export function getPageMeta(pathname: string): PageMeta {
  if (PAGE_META[pathname]) return PAGE_META[pathname];
  return { ...NOT_FOUND_META, path: pathname };
}

export function isKnownRoute(pathname: string): boolean {
  return pathname in PAGE_META;
}

export function getPageMetaResolved(pathname: string) {
  const meta = getPageMeta(pathname);
  const known = isKnownRoute(pathname);
  const canonical = known ? absoluteUrl(meta.path) : absoluteUrl(pathname);
  const ogImage = absoluteAsset(meta.ogImage ?? SITE_CONFIG.defaultOgImage);

  return {
    ...meta,
    canonical,
    ogImage,
    ogImageAlt: meta.ogImageAlt ?? SITE_CONFIG.defaultOgImageAlt,
    ogType: meta.ogType ?? 'website',
    robots: meta.robots ?? 'index, follow',
  };
}
